import { useEffect, useState } from 'react';
import { reference, type LocalStop } from '../lib/localdb.js';
import { addCapture } from '../lib/actions.js';
import { transition } from '../lib/actions.js';
import { text } from '../lib/format.js';
import { BigButton, CameraButton, Chip } from '../components/Controls.js';

/**
 * Why a visit fails.
 *
 * Reachable from *en route* as well as from the door, because a van that breaks down, an
 * accident or a road closed by construction fails the stop before anyone knocks — and the
 * record should say so, and say where.
 *
 * **The reasons are rows, never strings.** They come from the list the businesses already
 * maintain — *Client absent*, *Accès bloqué*, *Technicien blessé*, *Pas le matériel adéquat*,
 * *Machine encore propre* — each carrying a `kind` the app understands, so reporting works
 * across three businesses without matching French text.
 *
 * **Client absent has a procedure**, because it is the most common failure everywhere: call
 * from the app, a wait timer the business sets, a photograph of the door stamped with the time
 * and a position fix, and a spoken note. The outcome is *no-show with evidence*, which is what
 * defends a trip fee — and it is made entirely of pieces that exist for other reasons.
 */

type Reason = { id: string; kind: string; name: unknown; requiresNote: boolean; isNoShow: boolean };
type Settings = { noShowWaitMinutes?: number };

export function CannotDoPanel({ stop, onDone }: { stop: LocalStop; onDone: () => void }) {
  const [reasons, setReasons] = useState<Reason[]>([]);
  const [waitMinutes, setWaitMinutes] = useState(15);
  const [reasonId, setReasonId] = useState<string | null>(null);
  const [note, setNote] = useState('');
  const [doorMediaId, setDoorMediaId] = useState<string | null>(null);
  const [waitStartedAt, setWaitStartedAt] = useState<number | null>(null);
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    void (async () => {
      setReasons(await reference<Reason[]>('reasons', []));
      const settings = await reference<Settings>('settings', {});
      setWaitMinutes(settings.noShowWaitMinutes ?? 15);
    })();
  }, []);

  useEffect(() => {
    if (waitStartedAt === null) return;
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, [waitStartedAt]);

  const reason = reasons.find((candidate) => candidate.id === reasonId) ?? null;
  const waited = waitStartedAt === null ? 0 : Math.floor((now - waitStartedAt) / 1000);
  const remaining = Math.max(0, waitMinutes * 60 - waited);
  const noteNeeded = reason?.requiresNote && !note.trim();
  const evidenceNeeded = reason?.isNoShow && !doorMediaId;
  const phone = stop.phones[0];

  return (
    <div className="cannot">
      <h3>Pourquoi le travail n’a pas pu être fait</h3>
      <div className="chips">
        {reasons.map((candidate) => (
          <Chip
            key={candidate.id}
            tone={candidate.kind === 'safety' ? 'danger' : candidate.kind === 'technician' ? 'warm' : 'neutral'}
            on={reasonId === candidate.id}
            onToggle={() => setReasonId(candidate.id)}
          >
            {text(candidate.name)}
          </Chip>
        ))}
      </div>

      {/* The procedure, offered only where it applies. */}
      {reason?.isNoShow && (
        <section className="cannot__procedure">
          <h4>Client absent</h4>
          {phone && (
            <a className="ghost ghost--big" href={`tel:${phone.normalized}`}>
              Appeler le client
            </a>
          )}

          {waitStartedAt === null ? (
            <BigButton tone="quiet" onClick={() => setWaitStartedAt(Date.now())}>
              Démarrer l’attente de {waitMinutes} min
            </BigButton>
          ) : (
            <p className="reading__value tnum">
              {remaining > 0
                ? `${Math.floor(remaining / 60)}:${String(remaining % 60).padStart(2, '0')}`
                : 'Attente terminée'}
            </p>
          )}

          <CameraButton
            tone={doorMediaId ? 'quiet' : 'warm'}
            onFile={async (file) => {
              // Stamped with the time and a position fix: this is the record that defends a
              // trip fee, and it is worth nothing without both.
              const saved = await addCapture({
                appointmentId: stop.id,
                kind: 'attempted_visit',
                blob: file,
                withPosition: true,
                caption: 'Porte / numéro civique',
              });
              setDoorMediaId(saved.id);
            }}
          >
            {doorMediaId ? '✓ Photo de la porte' : 'Photo de la porte ou du numéro civique'}
          </CameraButton>
          <p className="quiet">L’office voit la tentative dès que le signal revient et peut joindre le client.</p>
        </section>
      )}

      <label className="field">
        <span>Note{reason?.requiresNote ? ' (obligatoire)' : ''}</span>
        <textarea rows={3} value={note} onChange={(event) => setNote(event.target.value)} />
      </label>

      <BigButton
        tone="danger"
        disabled={!reasonId || Boolean(noteNeeded) || Boolean(evidenceNeeded)}
        onClick={async () => {
          await transition(stop.id, 'cannot_do', { reasonId, note: note.trim() || null });
          onDone();
        }}
      >
        Enregistrer et quitter
      </BigButton>
      {evidenceNeeded && <p className="quiet">Une photo de la porte est ce qui défend des frais de déplacement.</p>}
    </div>
  );
}
